'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FAQItem {
  question: string;
  answer: string;
  category: string;
}

interface FAQSectionProps {
  className?: string;
}

const FAQSection = ({ className = '' }: FAQSectionProps) => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqs: FAQItem[] = [
    {
      question: 'How far is Proximus from IIM Bangalore?',
      answer: 'Proximus is located just 160 metres from IIM Bangalore South Gate — approximately a 1-minute drive or a short walk. This makes it the ideal base for business travelers attending conferences, workshops, executive education programs, or meetings at the institute.',
      category: 'Location',
    },
    {
      question: 'What are the nearest corporate hubs and tech parks?',
      answer: 'HSBC HDPI 200 m (2 min), SJR Tech Park is just 850 m away (4 min), Electronic City is 17 km (25 min), Kalyani Magnum 2.5 km (10 min) and Koramangala Business District is 7 km (25 min). Honeywell , Bosch and other MNCs are within 1.5 km. Over 15 major corporate campuses are within a 30-minute radius.',
      category: 'Business',
    },
    {
      question: 'How do I get to Kempegowda International Airport?',
      answer: 'The airport is approximately 45 km away by road, typically a 60–75 minute drive depending on traffic. We offer airport transfer services and can arrange taxi or cab pickups. BMTC Vayu Vajra airport buses also connect from nearby Bilekahalli stop.',
      category: 'Transportation',
    },
    {
      question: 'Are there good dining options nearby?',
      answer: 'Yes! Dozens of restaurants are within 2–4 km. Nagarjuna Restaurant (Andhra cuisine, 1.8 km), Rameshwaram Cafe (South Indian, 3.5 km), Chavadi (multi-cuisine), and Single Teas are all close by. The food court at Nexus Vega City Mall (1.7 km) offers 20+ cuisine options.',
      category: 'Dining',
    },
    {
      question: 'What entertainment and leisure activities are available?',
      answer: 'Nexus Vega City Mall (1.7 km) has PVR Cinema, FunCity Arcade, and 100+ stores. Bannerghatta National Park (11 km) offers wildlife safaris. Turahalli Forest (10 km) is great for trekking. Art of Living International Center (12 km) offers wellness retreats. Innovative Film City is 35 km away via Mysore Road.',
      category: 'Entertainment',
    },
    {
      question: 'Is public transportation easily accessible?',
      answer: 'Yes, BMTC bus stops are within 300–500 m on Bannerghatta Road with frequent services. Yelachenahalli Metro Station (Green Line) is 3.4 km away and JP Nagar Metro Station is 3.5 km away. Auto-rickshaws, Ola, and Uber are available 24/7 right outside the property.',
      category: 'Transportation',
    },
    {
      question: 'What medical facilities are nearby?',
      answer: 'Apollo Hospitals (Bannerghatta Road) is just 800 metres away — a 2-minute walk — and operates 24/7. Fortis Hospital (Bannerghatta Road, opposite IIM) is approximately 500 m away. Both are full-service multispecialty hospitals with emergency care. Multiple 24-hour pharmacies are also within 300 m.',
      category: 'Healthcare',
    },
    {
      question: 'Are there shopping centers and supermarkets nearby?',
      answer: 'SPAR Hypermarket is located inside Nexus Vega City Mall, just 1.7 km away — ideal for daily groceries and household needs. STAR Market Ranka Colony is approximately 3 km away. Reliance Market Mall is 2.1 km away in Royal Meenakshi Mall. Local convenience stores and vegetable markets are within walking distance in Arekare market.',
      category: 'Shopping',
    },
  ];

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className={`py-16 lg:py-24 bg-white ${className}`}>
      <div className="max-w-4xl mx-auto px-4 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="font-display text-3xl lg:text-5xl font-bold text-text-primary mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-lg text-text-secondary">
            Everything you need to know about the Proximus location and neighborhood
          </p>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="bg-card rounded-xl shadow-subtle border border-border overflow-hidden"
            >
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-muted transition-colors duration-300"
              >
                <div className="flex-1 pr-4">
                  <span className="inline-block px-3 py-1 bg-primary/10 text-primary text-xs font-medium rounded-full mb-2">
                    {faq.category}
                  </span>
                  <h3 className="font-display text-lg font-semibold text-text-primary">
                    {faq.question}
                  </h3>
                </div>
                <Icon
                  name={openIndex === index ? 'ChevronUpIcon' : 'ChevronDownIcon'}
                  size={24}
                  className="text-text-secondary flex-shrink-0"
                />
              </button>
              
              {openIndex === index && (
                <div className="px-6 pb-5 animate-slide-in-left">
                  <p className="text-text-secondary leading-relaxed">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-12 text-center">
          <p className="text-text-secondary mb-4">Still have questions?</p>
          <button className="px-8 py-3 bg-primary text-white font-semibold rounded-lg shadow-subtle hover:shadow-elevated hover:bg-primary/90 transition-all duration-300">
            Contact Our Team
          </button>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;